import { Box } from '@mui/material';
import { useCallback, useState, type ReactNode, type RefObject } from 'react';
import { useStudio } from '../../context';
import { RULER_SIZE, StudioRuler } from './StudioRuler';

interface StudioRulersProps {
  canScrollX: boolean;
  canScrollY: boolean;
  children: ReactNode;
  designHeight: number;
  designWidth: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  scrollRef?: RefObject<HTMLDivElement | null>;
  stageHeight: number;
  stageWidth: number;
}

export const StudioRulers = ({
  canScrollX,
  canScrollY,
  children,
  designHeight,
  designWidth,
  offsetX,
  offsetY,
  scale,
  scrollRef,
  stageHeight,
  stageWidth,
}: StudioRulersProps) => {
  const { showRulers, zoom } = useStudio();

  const [scroll, setScroll] = useState({ left: 0, top: 0 });

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!showRulers) return;
      const target = e.currentTarget;
      setScroll({ left: target.scrollLeft, top: target.scrollTop });
    },
    [showRulers],
  );

  return (
    <Box
      display="grid"
      width="100%"
      height="100%"
      sx={{
        gridTemplateColumns: showRulers ? `${RULER_SIZE}px 1fr` : '1fr',
        gridTemplateRows: showRulers ? `${RULER_SIZE}px 1fr` : '1fr',
        userSelect: 'none',
      }}
    >
      {showRulers && (
        <>
          {/* Corner square */}
          <Box
            sx={{
              backgroundColor: 'background.default',
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'borders.light',
            }}
          />

          {/* Horizontal ruler (top) */}
          <Box sx={{ borderBottom: 1, borderColor: 'borders.light' }}>
            <StudioRuler
              designSize={designWidth}
              offset={offsetX}
              orientation="horizontal"
              scale={scale}
              scrollOffset={scroll.left}
              stageSize={stageWidth}
              zoom={zoom}
            />
          </Box>

          {/* Vertical ruler (left) */}
          <Box height="100%" sx={{ borderRight: 1, borderColor: 'borders.light' }}>
            <StudioRuler
              designSize={designHeight}
              offset={offsetY}
              orientation="vertical"
              scale={scale}
              scrollOffset={scroll.top}
              stageSize={stageHeight}
              zoom={zoom}
            />
          </Box>
        </>
      )}

      {/* Canvas container */}
      <Box
        ref={scrollRef}
        position="relative"
        height="100%"
        onScroll={handleScroll}
        sx={{
          overflowX: canScrollX ? 'auto' : 'hidden',
          overflowY: canScrollY ? 'auto' : 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
